import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/store";
import { HolidayFormModal } from "../create-holiday";
import { IHoliday } from "src/slices/holidays/types";
import { selectHolidayList } from "src/slices/holidays/selectors";
import {
  deleteHoliday,
  fetchHolidayList,
  upsertHoliday,
} from "src/slices/holidays/thunks";

export const HolidayPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const holidayRows = useSelector(selectHolidayList);

  const [openHolidayModal, setOpenHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<IHoliday | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchHolidayList());
  }, [dispatch]);

  const holidayColumns: MRT_ColumnDef<IHoliday>[] = [
    { accessorKey: "name", header: "Holiday Name" },
    { accessorKey: "startDate", header: "Start Date" },
    { accessorKey: "endDate", header: "End Date" },
    {
      accessorKey: "isRecurring",
      header: "Recurring",
      Cell: ({ cell }) => (cell.getValue<boolean>() ? "Yes" : "No"),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      Cell: ({ cell }) => {
        const isActive = cell.getValue<boolean>();
        return (
          <Typography
            sx={{
              color: isActive ? "green" : "red",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </Typography>
        );
      },
    },
  ];

  const handleSubmitHoliday = async (data: any) => {
    const payload = {
      id: editingHoliday?.id,
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      isRecurring: data.isRecurring || false,
    };
    await dispatch(upsertHoliday(payload));
    dispatch(fetchHolidayList());
  };

  const handleDelete = async () => {
    await dispatch(deleteHoliday(selectedRecord.id));
    dispatch(fetchHolidayList());
    setOpenConfirm(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <MaterialReactTable
        columns={holidayColumns}
        data={holidayRows || []}
        enableRowActions
        renderRowActionMenuItems={({ row, closeMenu }) => [
          <MenuItem
            key="edit"
            onClick={() => {
              closeMenu();
              setEditingHoliday(row.original);
              setOpenHolidayModal(true);
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              closeMenu();
              setSelectedRecord(row.original);
              setOpenConfirm(true);
            }}
          >
            Delete
          </MenuItem>,
        ]}
        // ✅ Add Button inside Table Toolbar (LEFT SIDE)
        renderTopToolbarCustomActions={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              width: "100%",
              ml: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setEditingHoliday(null);
                setOpenHolidayModal(true);
              }}
            >
              Add Holiday
            </Button>
          </Box>
        )}
      />

      {/* Confirm Delete Dialog */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Holiday?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Holiday Form Modal */}
      <HolidayFormModal
        open={openHolidayModal}
        onClose={() => setOpenHolidayModal(false)}
        onSubmit={handleSubmitHoliday}
        initialValues={editingHoliday}
      />
    </Box>
  );
};
